import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiCheck, FiEdit3, FiExternalLink, FiLink, FiRefreshCw, FiX } from "react-icons/fi";
import "@bcc/core-renderers/styles.css";
import { PageRenderer, RendererRuntimeProvider, type All8PageContent } from "@bcc/core-renderers";
import { Modal } from "../Modal";
import { LoadingSpinner } from "../LoadingSpinner";
import { client } from "../../Clients";
import type { ContentDocument } from "../../API/Models";
import type { HrefTarget } from "../../types/layout";
import { createRuntimeImageComponent, resolveStorageUrl } from "../../Helper/storage";
import { PageBrowserPanel } from "./PageBrowserPanel";
import {
	buildPageHref,
	CATEGORY_FILTERS,
	DEFAULT_LANGUAGE,
	ensureLeadingSlash,
	formatDisplay,
	MOBILE_PREVIEW_WIDTH,
	normalizeComponents,
	PAGE_SIZE,
	resolveMode,
	type HrefInputProps,
	type Mode,
	type PageListItem
} from "./hrefInputUtils";

const runtimeImageComponent = createRuntimeImageComponent();
const runtimeValue = { imageComponent: runtimeImageComponent, assetResolver: resolveStorageUrl };

/**
 * Link field editor for HrefTarget values ({ slug } | { pageId } | { external }). Opens a modal
 * with up to three modes : browse real site pages, type a manual path, or paste an external URL : * plus a scaled mobile preview of the selected page rendered through the real SDK PageRenderer,
 * so editors can confirm the destination is the page they think it is. The choice is staged in
 * pendingValue and only reaches `onChange` on Confirm; browsing never dirties the parent form.
 * The allow* props let field configs restrict which modes make sense for a given link slot.
 */
const HrefInput: React.FC<HrefInputProps> = ({
	value,
	onChange,
	label,
	helper,
	disabled = false,
	allowExternal = true,
	allowManual = true,
	allowPage = true,
	className = ""
}) => {
	const availableModes = useMemo<Mode[]>(() => {
		const modes: Mode[] = [];
		if (allowPage) modes.push("page");
		if (allowManual) modes.push("manual");
		if (allowExternal) modes.push("external");
		return modes.length ? modes : ["manual"];
	}, [allowExternal, allowManual, allowPage]);

	const [open, setOpen] = useState(false);
	const [mode, setMode] = useState<Mode>(() => resolveMode(value ?? null, availableModes));
	const [pages, setPages] = useState<PageListItem[]>([]);
	const [loadingPages, setLoadingPages] = useState(false);
	const [pagesError, setPagesError] = useState<string | null>(null);
	const [pagesPage, setPagesPage] = useState(1);
	const [pagesTotal, setPagesTotal] = useState(0);
	const [search, setSearch] = useState("");
	const [categoryFilter, setCategoryFilter] = useState<string>("all");
	const [languageFilter, setLanguageFilter] = useState<string>("all");
	const [manualPath, setManualPath] = useState<string>(value?.external ? "" : value?.slug ?? "");
	const [externalValue, setExternalValue] = useState<string>(value?.external ?? "");
	const [pendingValue, setPendingValue] = useState<HrefTarget | null | undefined>(value ?? null);
	const [previewComponents, setPreviewComponents] = useState<All8PageContent>([]);
	const [previewLoading, setPreviewLoading] = useState(false);
	const [previewError, setPreviewError] = useState<string | null>(null);

	useEffect(() => {
		if (!open) return;
		setMode(resolveMode(value ?? null, availableModes));
		setManualPath(value?.external ? "" : value?.slug ?? "");
		setExternalValue(value?.external ?? "");
		setPendingValue(value ?? null);
		setPagesPage(1);
		setPreviewComponents([]);
		setPreviewError(null);
		setPreviewLoading(false);
	}, [open, value, availableModes]);

	// Two list endpoints on purpose: language-filtered browsing uses the page-list endpoint (the
	// language is a route segment there), while "all languages" goes through the generic content
	// list filtered to schema "page" : its documents are then mapped into the same PageListItem
	// shape so the browser panel doesn't care which path produced them.
	const loadPages = useCallback(async (pageNum: number) => {
		if (!allowPage) return;
		setLoadingPages(true);
		setPagesError(null);
		try {
			const filterDef = CATEGORY_FILTERS.find((f) => f.key === categoryFilter) ?? CATEGORY_FILTERS[0];
			const pathPrefix = filterDef.prefixes[0] ?? undefined;
			const searchTerm = search.trim() || undefined;
			const normalizedLanguage = languageFilter === "all" ? null : languageFilter;

			if (normalizedLanguage) {
				const response = await client.content.apiContentPageListGet(
					normalizedLanguage,
					pageNum,
					PAGE_SIZE,
					searchTerm,
					pathPrefix
				);
				const data = response.data ?? null;
				setPages((data?.items as PageListItem[]) ?? []);
				setPagesTotal(data?.totalCount ?? 0);
				setPagesPage(data?.page ?? pageNum);
			} else {
				const response = await client.content.apiContentListGet(
					pageNum,
					PAGE_SIZE,
					searchTerm,
					pathPrefix,
					"page"
				);
				const data = response.data ?? null;
				const items =
					(data?.items ?? []).map((doc) => {
						const meta = (doc.metadata ?? {}) as any;
						return {
							id: doc.id ?? null,
							slug: doc.slug ?? null,
							language: (doc.language ?? DEFAULT_LANGUAGE) as string,
							title: (doc.title as string | undefined) ?? null,
							metadata: meta ?? {}
						} as PageListItem;
					}) ?? [];
				setPages(items);
				setPagesTotal(data?.totalCount ?? 0);
				setPagesPage(data?.page ?? pageNum);
			}
		} catch (err) {
			console.error(err);
			setPagesError("Unable to load pages right now.");
			setPages([]);
			setPagesTotal(0);
			setPagesPage(1);
		} finally {
			setLoadingPages(false);
		}
	}, [allowPage, categoryFilter, languageFilter, search]);

	useEffect(() => {
		if (!open || !allowPage) return;
		loadPages(pagesPage);
	}, [open, allowPage, pagesPage, categoryFilter, languageFilter, search, loadPages]);

	const languages = useMemo(() => {
		const set = new Set<string>();
		pages.forEach((p) => set.add((p.language || DEFAULT_LANGUAGE).toLowerCase()));
		return Array.from(set).sort();
	}, [pages]);

	// Match the pending target back to a loaded page: pageId first (it survives slug renames),
	// slug comparison only as a fallback for values saved before ids were captured. Both sides are
	// normalized to leading-slash form so "/a/b" and "a/b" compare equal. Can legitimately be null
	// while the matching page is on another list page : the preview then falls back to the slug.
	const selectedPage = useMemo(() => {
		if (!pendingValue) return null;
		const slug = pendingValue.slug?.trim();
		const pageId = pendingValue.pageId?.trim();
		if (pageId) return pages.find((p) => p.id === pageId) ?? null;
		if (slug) return pages.find((p) => ensureLeadingSlash(p.slug || "") === ensureLeadingSlash(slug)) ?? null;
		return null;
	}, [pages, pendingValue]);

	const handleSelectPage = (page: PageListItem) => {
		const next = buildPageHref(page);
		setPendingValue(next);
	};

	const applyManualPath = () => {
		const cleaned = manualPath.trim();
		setPendingValue(cleaned ? { slug: ensureLeadingSlash(cleaned) } : null);
	};

	const applyExternal = () => {
		const cleaned = externalValue.trim();
		setPendingValue(cleaned ? { external: cleaned } : null);
	};

	const clearValue = () => {
		onChange(null);
		setManualPath("");
		setExternalValue("");
		setOpen(false);
	};

	const currentDisplay = formatDisplay(value);
	const pendingDisplay = formatDisplay(pendingValue);

	const hasPendingHref = Boolean(
		pendingValue && ((pendingValue.slug ?? "").trim() || (pendingValue.external ?? "").trim() || (pendingValue.pageId ?? "").trim())
	);

	const confirmSelection = () => {
		onChange(pendingValue ?? null);
		setOpen(false);
	};

	useEffect(() => {
		if (!open) return;
		// Only preview when a site page is selected (no external/manual without match).
		if (!pendingValue || pendingValue.external) {
			setPreviewComponents([]);
			setPreviewError(pendingValue?.external ? "External links cannot be previewed here." : null);
			setPreviewLoading(false);
			return;
		}
		const slugSource = selectedPage?.slug ?? pendingValue.slug ?? "";
		const slug = ensureLeadingSlash(slugSource).replace(/^\/+/, "");
		const lang = (selectedPage?.language ?? DEFAULT_LANGUAGE).toLowerCase();
		if (!slug) {
			setPreviewComponents([]);
			setPreviewError("Select a site page to load the preview.");
			setPreviewLoading(false);
			return;
		}

		// Stale-response guard: selections can change faster than fetches resolve, so each effect
		// instance owns a `cancelled` flag and only the latest one may write preview state.
		let cancelled = false;
		const fetchPreview = async () => {
			setPreviewLoading(true);
			setPreviewError(null);
			try {
				const response = await client.content.apiContentSchemaKeyLanguageGet("page", lang, slug);
				const vm: any = response.data ?? null;
				const currentDoc: ContentDocument | undefined = (vm?.current as ContentDocument) ?? undefined;
				const components = normalizeComponents((currentDoc?.data as any)?.components ?? []);
				if (!cancelled) {
					setPreviewComponents(components);
				}
			} catch (err) {
				console.error(err);
				if (!cancelled) {
					setPreviewComponents([]);
					setPreviewError("Unable to load page preview.");
				}
			} finally {
				if (!cancelled) setPreviewLoading(false);
			}
		};
		fetchPreview();

		return () => {
			cancelled = true;
		};
	}, [open, pendingValue, selectedPage]);

	const modeButtons = availableModes.map((m) => ({
		key: m,
		label: m === "page" ? "Site page" : m === "manual" ? "Manual path" : "External URL"
	}));

	return (
		<div className={`space-y-1 ${className}`}>
			{label && <label className="text-xs font-semibold text-slate-600">{label}</label>}
			<div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm">
				<FiLink className="text-slate-500" />
				<div className="flex-1 min-w-0 truncate">{currentDisplay}</div>
				{!disabled && (
					<>
						{value && (
							<button
								type="button"
								onClick={clearValue}
								className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-500 transition hover:border-slate-300 hover:text-slate-700"
								aria-label="Clear link"
							>
								<FiX />
							</button>
						)}
						<button
							type="button"
							onClick={() => setOpen(true)}
							className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-800"
						>
							<FiEdit3 /> Edit
						</button>
					</>
				)}
			</div>
			{helper && <p className="text-[11px] text-slate-500">{helper}</p>}

			<Modal show={open} onHide={() => setOpen(false)} size="full85" centered>
				<Modal.Header className="bg-slate-900 text-white" closeButton>
					<div className="flex items-center gap-2">
						<FiLink />
						<Modal.Title>Select destination</Modal.Title>
					</div>
				</Modal.Header>
				<Modal.Body className="bg-slate-50">
					<div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
						<div className="space-y-4">
							<div className="flex flex-wrap items-center justify-between gap-2">
								<div className="inline-flex items-center rounded-full border border-slate-200 bg-white p-1 text-xs font-semibold text-slate-700 shadow-sm">
									{modeButtons.map((btn) => {
										const isActive = mode === btn.key;
										return (
											<button
												key={btn.key}
												type="button"
												onClick={() => setMode(btn.key as Mode)}
												className={`px-3 py-1 rounded-full transition ${
													isActive ? "bg-slate-900 text-white shadow-sm" : "text-slate-700 hover:bg-slate-100"
												}`}
											>
												{btn.label}
											</button>
										);
									})}
								</div>
								{mode === "page" && allowPage && (
									<button
										type="button"
										onClick={() => loadPages(pagesPage)}
										className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-100"
									>
										<FiRefreshCw /> Refresh
									</button>
								)}
							</div>

							{mode === "page" && allowPage && (
								<PageBrowserPanel
									pages={pages}
									loadingPages={loadingPages}
									pagesError={pagesError}
									pagesPage={pagesPage}
									pagesTotal={pagesTotal}
									search={search}
									categoryFilter={categoryFilter}
									languageFilter={languageFilter}
									languages={languages}
									pendingValue={pendingValue}
									onSearchChange={setSearch}
									onCategoryFilterChange={setCategoryFilter}
									onLanguageFilterChange={setLanguageFilter}
									onSelectPage={handleSelectPage}
									onPageChange={setPagesPage}
								/>
							)}

							{mode === "manual" && allowManual && (
								<div className="space-y-2">
									<label className="text-xs font-semibold text-slate-600">Manual path</label>
									<div className="flex flex-wrap items-center gap-2">
										<input
											type="text"
											value={manualPath}
											onChange={(e) => setManualPath(e.target.value)}
											placeholder="/example-path"
											className="w-full flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
										/>
										<button
											type="button"
											onClick={applyManualPath}
											className="inline-flex items-center gap-1 rounded-md bg-slate-900 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-800"
										>
											Set path
										</button>
									</div>
									<p className="text-[11px] text-slate-500">Paths should include a leading slash.</p>
								</div>
							)}

							{mode === "external" && allowExternal && (
								<div className="space-y-2">
									<label className="text-xs font-semibold text-slate-600">External URL</label>
									<div className="flex flex-wrap items-center gap-2">
										<input
											type="url"
											value={externalValue}
											onChange={(e) => setExternalValue(e.target.value)}
											placeholder="https://example.com"
											className="w-full flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
										/>
										<button
											type="button"
											onClick={applyExternal}
											className="inline-flex items-center gap-1 rounded-md bg-slate-900 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-800"
										>
											<FiExternalLink /> Set URL
										</button>
									</div>
									<p className="text-[11px] text-slate-500">Include the protocol (https://).</p>
								</div>
							)}
						</div>

						<div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
							<div className="flex items-center justify-between gap-2">
								<div>
									<p className="text-xs uppercase tracking-[0.3em] text-slate-400">Preview</p>
									<p className="text-sm text-slate-600">Review the destination before confirming.</p>
								</div>
								{pendingValue?.external && (
									<span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-800">
										<FiExternalLink /> External
									</span>
								)}
								{pendingValue?.slug && !pendingValue?.external && (
									<span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-800">
										<FiLink /> Site page
									</span>
								)}
							</div>
							<div className="rounded-xl border border-slate-100 bg-slate-50 p-4 space-y-2">
								<p className="text-xs font-semibold text-slate-500">Destination</p>
								<p className="text-sm font-mono text-slate-800 break-all">{pendingDisplay}</p>
								{selectedPage && (
									<div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm space-y-1">
										<p className="text-sm font-semibold text-slate-900 truncate">{selectedPage.title || selectedPage.slug}</p>
										<p className="text-xs text-slate-600 truncate">{ensureLeadingSlash(selectedPage.slug || "")}</p>
										{selectedPage.metadata?.description && (
											<p className="text-xs text-slate-500 line-clamp-2">{selectedPage.metadata.description}</p>
										)}
									</div>
								)}
								{!hasPendingHref && <p className="text-xs text-slate-500">No destination selected yet.</p>}
							</div>

							<div className="space-y-2">
								<p className="text-xs font-semibold text-slate-500">Mobile preview</p>
								<div className="mx-auto w-full max-w-[440px] rounded-2xl border border-slate-200 bg-slate-900 p-3 shadow-lg">
									<div className="mx-auto h-6 w-16 rounded-full bg-slate-700/80" />
									<div className="mt-3 rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
										{previewLoading ? (
											<div className="flex h-[760px] items-center justify-center">
												<LoadingSpinner />
											</div>
										) : previewError ? (
											<div className="p-4 text-xs text-rose-600">{previewError}</div>
										) : previewComponents.length > 0 ? (
											<div className="mx-auto" style={{ width: `${MOBILE_PREVIEW_WIDTH}px` }}>
												<RendererRuntimeProvider value={runtimeValue}>
													<div className="bg-white">
														<PageRenderer components={previewComponents} />
													</div>
												</RendererRuntimeProvider>
											</div>
										) : (
											<div className="p-4 text-xs text-slate-500">Select a site page to see the live preview.</div>
										)}
									</div>
									<div className="mt-2 text-center text-[11px] text-slate-300">Scaled to mobile width</div>
								</div>
							</div>
						</div>
					</div>
				</Modal.Body>
				<Modal.Footer className="bg-white">
					<div className="flex flex-1 items-center justify-between gap-2">
						<div className="flex items-center gap-2">
							<button
								type="button"
								onClick={clearValue}
								className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
							>
								<FiX /> Clear
							</button>
						</div>
						<div className="flex items-center gap-2">
							<button
								type="button"
								onClick={() => setOpen(false)}
								className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
							>
								Close
							</button>
							<button
								type="button"
								onClick={confirmSelection}
								disabled={!hasPendingHref}
								className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
							>
								<FiCheck /> Confirm
							</button>
						</div>
					</div>
				</Modal.Footer>
			</Modal>
		</div>
	);
};

export default HrefInput;
